import React from 'react';
import { BsArchive, BsArrowLeftCircle, BsCheck2Circle, BsFileText, BsTrash } from 'react-icons/bs';

import ConfirmActionWarningModal from '../../../../components/ConfirmActionWarningModal';
import StackedButtonGroup from '../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import SaveChangesModal from '../../../../components/SaveChangesModal';
import { inAppText } from '../../../../constants/inAppText';
import { updateInsuranceRequirement } from '../../../../redux/modules/insuranceRequirements';
import {
  archiveInsuranceRequirement,
  deleteInsuranceRequirement,
  reactivateInsuranceRequirement
} from '../../../../redux/modules/jobs';
import { hideModal, showModal, updateModalOptions } from '../../../../redux/modules/modal';
import { hideTray, showTray, updateTrayProps } from '../../../../redux/modules/tray';
import { jobDetailsSearch } from '../../../../redux/reducer';
import insuredKeys from '../../../../services/models/insured/keys';
import { showDontAskAgainModal } from '../../../../utils/askAgainModalUtil';
import { confirmArchiveWithProcoreWarning, loadProcoreDeleteWarning } from '../../../../utils/procoreWarningModalUtil';
import { queryClient } from '../../../../utils/queryClientUtil';
import { createToast } from '../../../../utils/toastUtil';
import RecordWatcherIconButtonContainer from '../../../V2InsuredProfilePage/components/Records/RecordsBody/RecordWatcherIconButtonContainer';
import RequirementInfoTray from '../RequirementInfoTray';
import TransferRecordProjectModal from '../TransferRecordProjectModal';

const handleDeleteRecord = async ({ e, dispatch, insured }) => {
  e.preventDefault();
  e.stopPropagation();
  e.persist();

  const requirementId = parseInt(e.target.value, 10);
  const { procoreWarning, commit } = await loadProcoreDeleteWarning(dispatch, 'record');

  dispatch(showModal(
    () => (
      <ConfirmActionWarningModal
        description={inAppText.projectDetailsPage.recordOptions.deleteRecordMessage}
        procoreWarning={procoreWarning}
      />
    ),
    {
      title: 'Delete Record?',
      size: 'md',
      buttons: {
        component: StackedButtonGroup,
        primaryButtonOptions: {
          label: 'Delete',
          isDisabled: true,
          onClick: async () => {
            commit();
            dispatch(updateModalOptions({ buttons: { primaryButtonOptions: { isLoading: true } } }));

            await dispatch(deleteInsuranceRequirement(requirementId));
            dispatch(hideModal());
          }
        }
      }
    }
  ));
};

export const activeMenuOptions = (insuredRecord, companyId, currentUser) => ([
  {
    label: 'Record Information',
    icon: BsFileText,
    id: 'recordInformation',
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();
      const handleFormClose = formTrayProps => {
        if (formTrayProps.formIsDirty) {
          dispatch(showModal(
            () => (
              <SaveChangesModal changedInfo="record information" />
            ),
            {
              title: 'Exit without Saving?',
              buttons: {
                component: StackedButtonGroup,
                primaryButtonOptions: {
                  onClick: () => {
                    dispatch(hideModal());
                    dispatch(hideTray());
                  }
                }
              }
            }
          ));
        } else {
          dispatch(hideTray());
        }

        dispatch(updateTrayProps({}));
      };

      const onFormSubmit = async (requirement, requirementData) => {
        await dispatch(updateInsuranceRequirement(requirement.requirementId, requirementData));
        dispatch(jobDetailsSearch.getPage());
        const { id: insuredId } = insuredRecord;
        queryClient.invalidateQueries(insuredKeys.insuredAllRecordQueriesKey(insuredId));
      };

      dispatch(showTray(() => <RequirementInfoTray requirement={insuredRecord} onFormSubmit={onFormSubmit} />, {
        title: 'Record Information',
        titleComponent: () => (
          <RecordWatcherIconButtonContainer
            companyId={companyId}
            currentUser={currentUser}
            insuredId={insuredRecord.insuredId}
            recordId={insuredRecord.requirementId}
          />
        ),
        onClose: handleFormClose,
        props: { formIsDirty: false }
      }));
    }
  },
  {
    label: 'Move to Different Project',
    icon: BsArrowLeftCircle,
    id: 'moveRecord',
    handler: ({ e, dispatch, blueprint, insured, history }) => {
      e.preventDefault();
      e.stopPropagation();

      dispatch(showModal(
        TransferRecordProjectModal,
        {
          title: 'Transfer Record to New Project',
          size: 'xl'
        },
        { companyId, recordId: insuredRecord.requirementId, jobId: insuredRecord.projectId }
      ));
    }
  },
  {
    label: 'Archive Record',
    icon: BsArchive,
    id: 'archiveRecord',
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();
      e.persist();

      const cookieId = 'hideArchiveRecordModal';
      const recordId = parseInt(e.target.value, 10);

      const archiveRecord = () => {
        dispatch(hideModal());
        createToast({
          title: 'Archiving Record...',
          status: 'info',
          duration: 10000
        });

        dispatch(archiveInsuranceRequirement(recordId));
      };

      confirmArchiveWithProcoreWarning({
        dispatch,
        scope: 'record',
        cookieId,
        description: inAppText.projectDetailsPage.recordOptions.archiveRecordMessage,
        onClick: archiveRecord,
        title: 'Archive Record?'
      });
    }
  },
  {
    label: 'Delete Record',
    icon: BsTrash,
    id: 'deleteRecord',
    handler: handleDeleteRecord
  }
]);

export const archivedMenuOptions = [
  {
    label: 'Reactivate Record',
    icon: BsCheck2Circle,
    id: 'reactivateInsured',
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();
      e.persist();

      const cookieId = 'hideActivateRecordModal';
      const recordId = parseInt(e.target.value, 10);

      const activateRecord = () => {
        dispatch(hideModal());
        createToast({
          title: 'Reactivating Record...',
          status: 'info',
          duration: 10000
        });

        dispatch(reactivateInsuranceRequirement(recordId));
      };

      showDontAskAgainModal({
        dispatch,
        cookieId,
        description: inAppText.projectDetailsPage.recordOptions.reactivateRecordMessage,
        onClick: activateRecord,
        title: 'Reactivate Record?'
      });
    }
  },
  {
    label: 'Delete Record',
    icon: BsTrash,
    id: 'deleteInsured',
    handler: handleDeleteRecord
  }
];
